import { sortBy, filter } from "lodash";
import { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import { useLocation, useParams } from "react-router-dom";
import {
  addUseToPublicCollection,
  addUserToCollection,
  deleteCollection,
  deleteSharedUser,
  getBookmarksFromCollection,
  getCollectionOverview,
  getCollections,
  getParentChainCollection,
  getSharedUserList,
  publicCollection,
  removeAllSharedUsers,
  saveCollectionFolder,
  saveCustomBookmark,
  saveCollectionFolderWithNotes,
} from "../services/collection.service";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import {
  setCollectionIdArray,
  setCollections,
  setIsBookmarkSidebar,
  setParentCollections,
  setReadingSummary,
  setSummary,
} from "context/features/dataSlice";
import {
  getCollectionsSource,
  getParentCollections,
} from "features/Bookmarks/services/collection.service";

const useCollection = (setLoading, selectedCollectionOption = "bookmarks") => {
  const [bookmarks, setBookmarks] = useState([]);
  const [filteredBookmarks, setFilteredBookmarks] = useState([]);
  const [currentCollection, setCurrentCollection] = useState({});
  const [parentCollectionIds, setParentCollectionIds] = useState([]);
  // const [collectionIdArray, setCollectionIdArray] = useState([]);
  const [markdownNotes, setMarkdownNotes] = useState("");
  const previousId = useRef(null);

  const {
    collections,
    isSidebarOpen,
    collectionSourceId,
    collectionId,
    isBookmarkSidebar,
    collectionIdArray,
  } = useSelector((state) => state.data);

  const params = useParams();

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const searchUrlParams = searchParams.get("search")
    ? searchParams.get("search")
    : "";
  const [search] = useState(searchUrlParams);

  const dispatch = useDispatch();

  const { user } = useSelector((state) => state.auth);
  const { id } = useParams();

  const getBookmarkData = async () => {
    try {
      const data = await getCollectionsSource(collectionId, collectionSourceId);
      if (data[0].isSummaryAvailable_ysn)
        dispatch(setSummary(data[0].summary_chr));
      if (data[0].isReadingLevelAvailable_ysn)
        dispatch(setReadingSummary(data[0].articleComprehension_chr));
    } catch (e) {
      console.error(e);
    }
  };

  const getBookmarks = async (id, showLoader = true) => {
    if (!id) return;
    try {
      if (showLoader) {
        setLoading?.(true);
        setBookmarks([]);
        setFilteredBookmarks([]);
      }
      const data = await getBookmarksFromCollection(user, id);
      setBookmarks(data);
      setFilteredBookmarks(data);
    } catch (e) {
      console.error(e);
    } finally {
      if (showLoader) setLoading?.(false);
    }
  };

  const redirectAndSetHeaderParams = (data) => {
    if (!id) {
      setCurrentCollection(data[0]);
    } else {
      let item = data.find((item) => item.collectionId === Number(id));
      if (item) {
        item = {
          ...item,
          collectionName_chr: item.name,
        };
        setCurrentCollection(item);
      }
    }
  };

  const getParentCollectionIds = (data) => {
    const ids = data
      .filter((collection) => collection.parentCollectionID_lng === 0)
      .map((collection) => collection.collectionID_ids);
    setParentCollectionIds(ids);
  };

  const getData = async () => {
    const data = await getCollections(user);
    let newArr = [];
    sortBy(data, ["collectionId_ids"]).forEach((item) => {
      newArr.push({
        name: item.collectionName_chr,
        value: item.collectionName_chr,
        collectionId: item.collectionID_ids,
        parentCollectionId: item.parentCollectionID_lng,
        isShared_ysn: item.isShared_ysn,
        isPublic_ysn: item.isPublic_ysn,
      });
    });
    dispatch(setCollections(newArr));
    getParentCollectionIds(data);
    redirectAndSetHeaderParams(newArr);
  };

  const getCollArray = async () => {
    try {
      const data = await getParentChainCollection(id, user);
      const chain = data.map((item) => String(item));
      // console.log(data, chain);
      dispatch(setCollectionIdArray(chain));
    } catch (e) {
      console.error(e);
    }
  };

  const getUsersList = async () => {
    const data = await getSharedUserList(user, id);
    const filteredData = filter(
      data,
      (singleUser) => singleUser.email !== user.email
    );
    return filteredData;
  };

  const shareCollectionFolder = async (payload) => {
    try {
      await addUserToCollection(payload);

      if (payload.SharedWithTypeId === 2) {
        toast.success(
          `Collection shared to distribution list "${payload.UniqueDistributionListName}" successfully.`
        );
      } else {
        const emailCount = payload.Email?.length || 0;
        if (emailCount > 1) {
          toast.success(
            `Collection shared successfully with ${emailCount} users.`
          );
        } else {
          toast.success("Collection shared successfully.");
        }
      }

      getData();
    } catch (error) {
      console.error(error.response?.status);
      if (error.response?.status === 500) {
        toast.error("This Collection is already being shared with the user");
      }
      if (error.response?.data === "Recipient Not Found") {
        toast.error("User not found!");
      }
    }
  };

  const removeSharedUser = async (id, userToRemove) => {
    try {
      await deleteSharedUser(id, user, userToRemove);
      const isDistList = userToRemove?.sharedWithTypeId === 2;
      toast.success(
        isDistList
          ? "Distribution list removed successfully."
          : "Shared User access removed successfully."
      );
      getData();
    } catch (error) {
      if (error.response?.status === 500) {
        const identifier =
          userToRemove?.email ||
          userToRemove?.generatedDistributionListName ||
          "user";
        toast.error(
          `Unable to delete ${identifier} from shared collection. Kindly Contact admin for further support.`
        );
      }
    }
  };

  const handleDeleteCollection = async (id) => {
    try {
      await deleteCollection(user, id);
      getData();
      toast.success("Collection deleted successfully!");
    } catch (e) {
      console.error(e);
    }
  };

  const handleRenameCollection = async (id, name) => {
    try {
      await publicCollection(user, id, name);

      await getData();
      toast.success("Collection renamed successfully!");
    } catch (e) {
      console.error(e);
    }
  };

  const saveCollectionWithNotes = async (markdownNotes, showToastr = false) => {
    try {
      const data = await saveCollectionFolderWithNotes(
        user,
        markdownNotes,
        params.id
      );
      if (showToastr) {
        toast.success("Collection Notes saved successfully!");
      }
    } catch (e) {
      console.log(e);
      toast.error("Sorry couldn't save the collection. Please try again.");
    }
  };

  const saveCollection = async (name, subNewFolder, id = null) => {
    try {
      let parent = {};
      if (subNewFolder) {
        parent = { parentCollectionId: id };
      }
      const data = await saveCollectionFolder(user, name, parent);

      toast.success("Collection created successfully.");
      await getData();
      return data;
    } catch (e) {
      console.error(e);
      throw e;
    }
  };

  const fetchParentCollection = async () => {
    const data = await getParentCollections(user, 0);

    let newArr = [];
    data.forEach((item) =>
      newArr.push({
        name: item.collectionName_chr,
        value: item.collectionName_chr,
        collectionId: item.collectionID_ids,
        parentCollectionId: item.parentCollectionID_lng,
        isShared_ysn: item.isShared_ysn,
        isPublic_ysn: item.isPublic_ysn,
      })
    );
    dispatch(setParentCollections(newArr.reverse()));
  };

  const handleSaveCustomBookmark = async (bookmark, json_res) => {
    try {
      const data = await saveCustomBookmark(json_res);
      getBookmarks(id, false);
      toast.success("Saved to collection");
      dispatch(setIsBookmarkSidebar(false));
      return data;
    } catch (e) {
      toast.error("Something went wrong, please try later.");
      throw e;
    }
  };

  const handleRemoveAllSharedUsers = async () => {
    try {
      await removeAllSharedUsers(id, user);
      toast.success("Collection access to other users removed successfully!");
      getData();
    } catch (e) {
      console.error(e);
    }
  };

  const handleCollectionOverview = async () => {
    try {
      const data = await getCollectionOverview(user, id);
      setMarkdownNotes(data.note_chr);
      return data;
    } catch (e) {
      console.error(e);
    }
  };

  const makeCollectionPublic = async (isPublic = true) => {
    try {
      const data = await addUseToPublicCollection(user, id, isPublic);
      toast.success(
        `Collection made ${isPublic ? "public" : "private"} successfully.`
      );
      getData();
    } catch (e) {
      console.error(e);
    }
  };

  const handleGetData = () => {
    if (location.pathname.includes("/work-cited/") && !isSidebarOpen) {
      return;
    }
    redirectAndSetHeaderParams(collections);
    if (
      !location.pathname.includes("/public") &&
      selectedCollectionOption === "bookmarks"
    ) {
      if (id) getCollArray();
      if (!search && !isSidebarOpen && !isBookmarkSidebar) {
        let shouldShowLoader = bookmarks.length === 0;

        if (id !== previousId.current) {
          shouldShowLoader = true;
          previousId.current = id;
        }
        getBookmarks(id, shouldShowLoader);
      }
    }
  };

  return {
    parentCollectionIds,
    currentCollection,
    bookmarks,
    filteredBookmarks,
    setFilteredBookmarks,
    collectionIdArray,
    getData,
    fetchParentCollection,
    saveCollection,
    handleSaveCustomBookmark,
    getUsersList,
    shareCollectionFolder,
    removeSharedUser,
    makeCollectionPublic,
    handleDeleteCollection,
    handleRenameCollection,
    handleRemoveAllSharedUsers,
    handleCollectionOverview,
    setBookmarks,
    collections,
    markdownNotes,
    setMarkdownNotes,
    saveCollectionWithNotes,
    handleGetData,
    getBookmarkData,
  };
};

export default useCollection;
